import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertTriangle as WarningIcon,
  Heart as MedicalIcon,
  Hospital as HospitalIcon,
  Pill as MedicationIcon,
  Shield as SafetyIcon,
  Save as SaveIcon,
  Plus as AddIcon,
  Eye as VisibilityIcon,
  ShieldCheck as SecurityIcon,
} from 'lucide-react';
import { MedicalInfo } from '../../types/medical';

// 拡張MedicalInfo型（UI用に追加フィールドを含む）
type ExtendedMedicalInfo = MedicalInfo & {
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  treatmentNotes?: string;
  currentMedications?: string[];
};

interface MedicalInfoEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (medicalInfo: MedicalInfo) => void;
  initialData?: Partial<MedicalInfo>;
  customerName: string;
  customerId: string;
}

const MedicalInfoEditor: React.FC<MedicalInfoEditorProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  customerName,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState<ExtendedMedicalInfo>({
    allergies: initialData?.allergies || {
      food: [],
      medicine: [],
      cosmetic: [],
      other: []
    },
    medicalHistory: initialData?.medicalHistory || {
      bloodPressure: 'unknown',
      heartCondition: false,
      diabetes: false,
      epilepsy: false,
      pregnancy: false,
      breastfeeding: false,
      other: ''
    },
    medications: initialData?.medications || {
      current: [],
      bloodThinner: false,
      painMeds: false
    },
    contraindications: initialData?.contraindications || {
      headMassage: false,
      aromatherapy: false,
      hotTreatment: false,
      notes: ''
    },
    lastUpdated: initialData?.lastUpdated || new Date(),
    updatedBy: initialData?.updatedBy || customerName,
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    treatmentNotes: '',
    currentMedications: []
  });

  // 新しい項目を追加するための一時的な状態
  const [newAllergy, setNewAllergy] = useState('');
  const [newContraindication, setNewContraindication] = useState('');
  const [newMedicalHistory, setNewMedicalHistory] = useState('');
  const [newMedication, setNewMedication] = useState('');

  // よく使用される項目の事前定義
  const commonAllergies = [
    'ラベンダー系アロマ',
    '柑橘系アロマ',
    'ユーカリ系アロマ',
    'アルコール系化粧品',
    '金属アレルギー（ニッケル）',
    '金属アレルギー（クロム）',
    '香料全般',
    'ナッツ類',
  ];

  const commonContraindications = [
    'うつ伏せ制限（30分まで）',
    '首への強圧禁止',
    '血圧降下剤服用中',
    '妊娠中・妊娠の可能性',
    '手術後1ヶ月以内',
    '高血圧症',
    '心疾患',
    '皮膚疾患（湿疹・炎症）',
  ];

  const steps = [
    '基本医療情報',
    '緊急連絡先',
    '確認・保存'
  ];

  // 項目の追加ハンドラー
  const handleAddAllergy = () => {
    if (newAllergy.trim()) {
      // TODO: Implement allergy addition logic based on the correct type structure
      setNewAllergy('');
    }
  };

  const handleAddContraindication = () => {
    if (newContraindication.trim()) {
      // TODO: Implement contraindication addition logic based on the correct type structure
      setNewContraindication('');
    }
  };

  const handleAddMedicalHistory = () => {
    if (newMedicalHistory.trim()) {
      // TODO: Implement medical history addition logic based on the correct type structure
      setNewMedicalHistory('');
    }
  };

  const handleAddMedication = () => {
    if (newMedication.trim()) {
      setFormData(prev => ({
        ...prev,
        medications: {
          ...prev.medications,
          current: [...(prev.medications.current || []), newMedication.trim()]
        }
      }));
      setNewMedication('');
    }
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSave = () => {
    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    onSave(formData);
    setShowConfirmation(false);
    onClose();
  };

  const handleQuickAdd = (_category: keyof ExtendedMedicalInfo, _item: string) => {
    // TODO: Implement quick add logic based on the correct type structure
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            {/* アレルギー情報 */}
            <Card className="border-2 border-destructive">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <WarningIcon className="h-5 w-5" />
                  アレルギー・注意事項
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert variant="destructive">
                  <AlertDescription>
                    <strong>重要：</strong>施術前に必ず確認してください。命に関わる情報です。
                  </AlertDescription>
                </Alert>

                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    よく使用される項目から選択：
                  </Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {commonAllergies.map((allergy) => (
                      <Button
                        key={allergy}
                        size="sm"
                        variant="outline"
                        onClick={() => handleQuickAdd('allergies', allergy)}
                        disabled={
                          formData.allergies.food.includes(allergy) ||
                          formData.allergies.medicine.includes(allergy) ||
                          formData.allergies.cosmetic.includes(allergy) ||
                          formData.allergies.other.includes(allergy)
                        }
                      >
                        {allergy}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Input
                    placeholder="新しいアレルギー情報"
                    value={newAllergy}
                    onChange={(e) => setNewAllergy(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddAllergy();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddAllergy}
                    disabled={!newAllergy.trim()}
                  >
                    <AddIcon className="mr-2 h-4 w-4" />
                    追加
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {[...formData.allergies.food, ...formData.allergies.medicine, ...formData.allergies.cosmetic, ...formData.allergies.other].map((allergy, index) => (
                    <Badge
                      key={index}
                      variant="destructive"
                      className="text-sm"
                    >
                      {allergy}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 禁忌事項 */}
            <Card className="border-2 border-yellow-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-600">
                  <SafetyIcon className="h-5 w-5" />
                  禁忌事項・施術制限
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    よく使用される項目から選択：
                  </Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {commonContraindications.map((contraindication) => (
                      <Button
                        key={contraindication}
                        size="sm"
                        variant="outline"
                        onClick={() => handleQuickAdd('contraindications', contraindication)}
                      >
                        {contraindication}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Input
                    placeholder="新しい禁忌事項"
                    value={newContraindication}
                    onChange={(e) => setNewContraindication(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddContraindication();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddContraindication}
                    disabled={!newContraindication.trim()}
                  >
                    <AddIcon className="mr-2 h-4 w-4" />
                    追加
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {([] as string[]).map((contraindication, index) => (
                    <Badge
                      key={index}
                      className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                    >
                      {contraindication}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 既往症・現病歴 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HospitalIcon className="h-5 w-5" />
                  既往症・現病歴
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="既往症・現病歴"
                    value={newMedicalHistory}
                    onChange={(e) => setNewMedicalHistory(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddMedicalHistory();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddMedicalHistory}
                    disabled={!newMedicalHistory.trim()}
                  >
                    <AddIcon className="mr-2 h-4 w-4" />
                    追加
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {([] as string[]).map((history, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                    >
                      {history}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 服用薬 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MedicationIcon className="h-5 w-5" />
                  現在の服用薬
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="服用薬（薬剤名・用法・用量）"
                    value={newMedication}
                    onChange={(e) => setNewMedication(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddMedication();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddMedication}
                    disabled={!newMedication.trim()}
                  >
                    <AddIcon className="mr-2 h-4 w-4" />
                    追加
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {(formData.medications.current || []).map((medication, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="bg-blue-50 text-blue-700 border-blue-200"
                    >
                      {medication}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <SecurityIcon className="h-5 w-5" />
              <h3 className="text-lg font-semibold">緊急連絡先</h3>
            </div>

            <Alert>
              <AlertDescription>
                緊急連絡先の設定は任意です。設定いただくと、緊急時により安全な対応が可能になります。
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="emergencyName">緊急連絡先（氏名）※任意</Label>
              <Input
                id="emergencyName"
                value={formData.emergencyContact?.name || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  emergencyContact: {
                    name: e.target.value,
                    phone: prev.emergencyContact?.phone || '',
                    relationship: prev.emergencyContact?.relationship || ''
                  }
                }))}
                placeholder="例：山田花子"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyPhone">緊急連絡先（電話番号）※任意</Label>
              <Input
                id="emergencyPhone"
                value={formData.emergencyContact?.phone || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  emergencyContact: {
                    name: prev.emergencyContact?.name || '',
                    phone: e.target.value,
                    relationship: prev.emergencyContact?.relationship || ''
                  }
                }))}
                placeholder="例：090-1234-5678"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyRelationship">続柄・関係※任意</Label>
              <Input
                id="emergencyRelationship"
                value={formData.emergencyContact?.relationship || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  emergencyContact: {
                    name: prev.emergencyContact?.name || '',
                    phone: prev.emergencyContact?.phone || '',
                    relationship: e.target.value
                  }
                }))}
                placeholder="例：配偶者、親、友人など"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="treatmentNotes">施術注意事項・特記事項</Label>
              <Textarea
                id="treatmentNotes"
                value={formData.treatmentNotes}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  treatmentNotes: e.target.value
                }))}
                placeholder="施術時の特別な注意事項や要望があれば記載してください"
                rows={3}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <VisibilityIcon className="h-5 w-5" />
              <h3 className="text-lg font-semibold">入力内容の確認</h3>
            </div>

            <Alert className="border-yellow-500 bg-yellow-50">
              <AlertDescription>
                <strong>重要：</strong>以下の医療情報が施術時に自動表示され、スタッフに警告されます。
                内容に間違いがないか十分に確認してください。
              </AlertDescription>
            </Alert>

            {/* アレルギー情報の確認 */}
            {(formData.allergies.food.length + formData.allergies.medicine.length + formData.allergies.cosmetic.length + formData.allergies.other.length) > 0 && (
              <Card className="border-2 border-destructive">
                <CardContent className="pt-6">
                  <h4 className="text-destructive font-bold text-2xl mb-3 flex items-center gap-2">
                    <WarningIcon className="h-6 w-6" />
                    アレルギー・注意事項
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {[...formData.allergies.food, ...formData.allergies.medicine, ...formData.allergies.cosmetic, ...formData.allergies.other].map((allergy, index) => (
                      <Badge
                        key={index}
                        variant="destructive"
                        className="text-lg h-12 px-4 font-semibold"
                      >
                        {allergy}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 禁忌事項の確認 */}
            {(formData.contraindications.headMassage || formData.contraindications.aromatherapy || formData.contraindications.hotTreatment || formData.contraindications.notes) && (
              <Card className="border-2 border-yellow-500">
                <CardContent className="pt-6">
                  <h4 className="text-yellow-600 font-bold text-2xl mb-3 flex items-center gap-2">
                    🚫 禁忌事項・施術制限
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(formData.contraindications).filter(([k, v]) => v && k !== 'notes').map(([key], index) => (
                      <Badge
                        key={index}
                        className="bg-yellow-100 text-yellow-800 text-lg h-12 px-4 font-semibold"
                      >
                        {key}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* その他の情報 */}
            <Card>
              <CardContent className="pt-6 space-y-4">
                <h4 className="font-bold text-base">📋 その他の医療情報</h4>

                {(formData.medicalHistory.heartCondition || formData.medicalHistory.diabetes || formData.medicalHistory.epilepsy || formData.medicalHistory.pregnancy || formData.medicalHistory.breastfeeding || formData.medicalHistory.other) && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">既往症・現病歴：</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(formData.medicalHistory).filter(([k, v]) => v && k !== 'bloodPressure').map(([key], index) => (
                        <Badge key={index} variant="outline">
                          {key}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {formData.currentMedications && formData.currentMedications.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">現在の服用薬：</p>
                    <div className="flex flex-wrap gap-2">
                      {(formData.currentMedications || []).map((medication, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="bg-blue-50 text-blue-700 border-blue-200"
                        >
                          {medication}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm text-muted-foreground mb-1">緊急連絡先：</p>
                  <p className="text-sm">
                    {formData.emergencyContact?.name} ({formData.emergencyContact?.relationship})
                    <br />
                    {formData.emergencyContact?.phone}
                  </p>
                </div>

                {formData.treatmentNotes && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">施術注意事項：</p>
                    <p className="text-sm whitespace-pre-line">
                      {formData.treatmentNotes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MedicalIcon className="h-5 w-5" />
              医療情報管理 - {customerName}様
            </DialogTitle>
          </DialogHeader>

          {/* Custom Stepper */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {steps.map((label, index) => (
                <div key={label} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                      index <= activeStep
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'border-muted-foreground text-muted-foreground'
                    }`}>
                      {index + 1}
                    </div>
                    <span className={`text-xs mt-1 text-center ${
                      index <= activeStep ? 'text-primary font-medium' : 'text-muted-foreground'
                    }`}>
                      {label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`h-0.5 flex-1 ${
                      index < activeStep ? 'bg-primary' : 'bg-muted'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="py-4">
            {getStepContent(activeStep)}
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <div>
              <Button onClick={onClose} variant="outline">
                キャンセル
              </Button>
            </div>
            <div className="flex gap-2">
              {activeStep > 0 && (
                <Button onClick={handleBack} variant="outline">
                  戻る
                </Button>
              )}
              {activeStep < steps.length - 1 ? (
                <Button onClick={handleNext}>
                  次へ
                </Button>
              ) : (
                <Button
                  onClick={handleSave}
                  variant="destructive"
                >
                  <SaveIcon className="mr-2 h-4 w-4" />
                  保存（2段階確認）
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2段階確認ダイアログ */}
      <Dialog open={showConfirmation} onOpenChange={() => setShowConfirmation(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader className="bg-destructive text-destructive-foreground p-4 -mx-6 -mt-6 mb-4 rounded-t-lg">
            <DialogTitle className="flex items-center gap-2 text-white">
              <SecurityIcon className="h-5 w-5" />
              医療情報保存の最終確認
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>
                <strong>重要な医療情報を保存します。</strong><br />
                この情報は施術時に自動的にスタッフに警告表示されます。
              </AlertDescription>
            </Alert>

            <p className="text-sm">
              以下の内容で保存してよろしいですか？
            </p>

            <div className="bg-muted p-4 rounded-md space-y-3">
              <p className="text-sm font-semibold mb-2">保存される項目：</p>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="font-medium">アレルギー情報: {formData.allergies.food.length + formData.allergies.medicine.length + formData.allergies.cosmetic.length + formData.allergies.other.length}件</p>
                  <p className="text-muted-foreground text-xs">
                    {[...formData.allergies.food, ...formData.allergies.medicine, ...formData.allergies.cosmetic, ...formData.allergies.other].join(', ') || 'なし'}
                  </p>
                </div>
                <div>
                  <p className="font-medium">禁忌事項: {Object.entries(formData.contraindications).filter(([k, v]) => k !== 'notes' && v).length + (formData.contraindications.notes ? 1 : 0)}件</p>
                  <p className="text-muted-foreground text-xs">
                    {Object.entries(formData.contraindications).filter(([k, v]) => v && k !== 'notes').map(([k]) => k).join(', ') || 'なし'}
                  </p>
                </div>
                <div>
                  <p className="font-medium">既往症: {Object.entries(formData.medicalHistory).filter(([k, v]) => k !== 'bloodPressure' && k !== 'other' && v).length + (formData.medicalHistory.other ? 1 : 0)}件</p>
                  <p className="text-muted-foreground text-xs">
                    {Object.entries(formData.medicalHistory).filter(([k, v]) => v && k !== 'bloodPressure').map(([k]) => k).join(', ') || 'なし'}
                  </p>
                </div>
                <div>
                  <p className="font-medium">服用薬: {formData.currentMedications?.length || 0}件</p>
                  <p className="text-muted-foreground text-xs">
                    {formData.currentMedications?.join(', ') || 'なし'}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              保存後、この情報は予約時や施術時に自動的にスタッフに表示され、
              安全な施術のために活用されます。
            </p>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowConfirmation(false)} variant="outline">
              キャンセル
            </Button>
            <Button
              onClick={handleConfirmSave}
              variant="destructive"
            >
              確定保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MedicalInfoEditor;
